import type { MovementType, TransactionStatus, TransactionType } from '@seccl/client';

export enum TransactionMovementEnum {
  In = 'In',
  Out = 'Out',
}

export enum TransactionSortEnum {
  CreatedDateDesc = 'createdDate:desc',
  CreatedDateAsc = 'createdDate:asc',
  LastUpdatedDateDesc = 'lastUpdatedDate:desc',
  LastUpdatedDateAsc = 'lastUpdatedDate:asc',
  TransactionDateDesc = 'transactionDate:desc',
  TransactionDateAsc = 'transactionDate:asc',
  CompletedDateDesc = 'completedDate:desc',
  CompletedDateAsc = 'completedDate:asc',
}

/**
 * Display context for the transactions table.
 * Determines which columns and filters are shown.
 */
export enum TransactionTableContext {
  /** Full business/adviser view with all columns and filters */
  Business = 'business',
  /** Client-specific view - hides clientId and businessOrAdviser columns/filters */
  Client = 'client',
  /** Account-specific view - hides clientId, accountId, and businessOrAdviser columns/filters */
  Account = 'account',
}

/**
 * Filter type for the legacy transactions table (v1 API).
 *
 * The legacy v1 API endpoint only supports a single status string value.
 * This table will be removed once the feature flag 'seccl-31363-transactions-enhanced-ui'
 * is enabled for all firms and the v2 API becomes the standard.
 *
 * @deprecated Will be removed when legacy table is fully deprecated. See LegacyTransactionsTable.
 */
export interface ILegacyTransactionFilters {
  accountId?: string;
  businessAdviserId?: number;
  type?: {
    transaction?: TransactionType;
    movement?: TransactionMovementEnum;
    transactionSubType?: string[];
    method?: string[];
  };
  status?: {
    /**
     * Single status value (v1 API constraint).
     * The v1 endpoint only accepts one status string, not an array.
     */
    transaction?: TransactionStatus;
    incomplete?: boolean | null;
  };
  dateRange?: {
    start?: string;
    end?: string;
  };
}

/**
 * Filter type for the enhanced transactions table (v2 API).
 *
 * The v2 API endpoint supports multiple status values via array.
 * This is the standard filter type going forward once 'seccl-31363-transactions-enhanced-ui'
 * feature flag is enabled for all firms.
 *
 * Split from ILegacyTransactionFilters because:
 * - v1 API: status parameter is a single string
 * - v2 API: status parameter is an array of strings or single string
 * Keeping them separate ensures type safety and clear deprecation path for legacy code.
 */
export interface ITransactionFilters {
  id?: string;
  /**
   * Filter to a specific set of transaction ids (e.g. deep-linked from the cash position panel).
   * Held as a comma-separated string so it round-trips through the routed-table URL state; it is
   * split into an array when passed to the transactions query.
   */
  ids?: string;
  linkId?: string;
  clientId?: string;
  accountId?: string;
  assetId?: string;
  movementType?: MovementType;
  type?: {
    transaction?: TransactionType;
    movement?: TransactionMovementEnum;
    transactionSubType?: string[];
    method?: string[];
  };
  businessAdviserId?: number;
  status?: {
    /**
     * Array of status values (v2 API feature).
     * The v2 endpoint supports multi-select status filtering.
     */
    transaction?: TransactionStatus[];
    incomplete?: boolean | null;
  };
  /**
   * Transaction date range filter. Mapped to the `transactionDateFrom`/`transactionDateTo`
   * query parameters on the transactions endpoint (used by both the legacy and enhanced tables).
   */
  dateRange?: {
    start?: string;
    end?: string;
  };
  createdDateRange?: {
    start?: string;
    end?: string;
  };
  completedDateRange?: {
    start?: string;
    end?: string;
  };
  lastUpdatedRange?: {
    start?: string;
    end?: string;
  };
}

/**
 * Data structure for exporting transaction records to CSV.
 * Contains all transaction fields formatted for export.
 */
export interface ITransactionExportRow {
  id: string;
  transactionDate: string;
  createdDate: string;
  completedDate: string;
  lastStatusChangeDate: string;
  account: string;
  accountId: string;
  accountType: string;
  client: string;
  clientId: string;
  type: string;
  transactionType: string;
  movementType: string;
  subType: string;
  method: string;
  asset: string;
  isin: string;
  ticker: string;
  amount: string;
  quantity: string;
  status: string;
  businessOrAdviser: string;
  nodeId: string;
  linkId: string;
  intendedSettlementDate: string;
}
